import { RefObject } from 'react'
import { stagger, Variants } from 'motion/react'
import tokens from '../../styles/tokens.json'

// Every timing of the menu's animation in seconds, the panel, its content and the backdrop all follow these.
// The panel grows out of the menu button and the content slides in one element after the other,
// on close the content fades out at once and the panel shrinks back into the button
export const menuTiming = {
  open: {
    panel: { duration: 0.5, ease: [0.2, 0, 0, 1] },
    content: { duration: 0.4, ease: [0, 0, 0.2, 1] },
    // the content starts once the panel has mostly grown
    contentDelay: 0.15,
    contentStagger: 0.05,
  },
  close: {
    panel: { delay: 0.1, duration: 0.4, ease: [0, 0, 0.2, 1] },
    content: { duration: 0.15, ease: [0.4, 0, 0.2, 1] },
  },
} as const

// in milliseconds for MUI's Backdrop, its default is ~200ms
export const backdropTransitionDuration = {
  enter: menuTiming.open.panel.duration * 1000,
  exit: (menuTiming.close.panel.delay + menuTiming.close.panel.duration) * 1000,
}

// clip-paths of the panel cut down to the menu button and grown to its own box
export type PanelShape = { collapsed: string; expanded: string }

// functions, so they pick up the shape measured right before the animation starts
export const getPanelVariants = (
  shape: RefObject<PanelShape | null>,
  shouldReduceMotion: boolean | null,
): Variants => ({
  collapsed: () => ({
    clipPath: shape.current?.collapsed,
    backgroundColor: tokens.colors.black95,
    transition: shouldReduceMotion ? { duration: 0 } : menuTiming.close.panel,
  }),
  expanded: () => ({
    clipPath: shape.current?.expanded,
    backgroundColor: tokens.colors.black,
    transition: {
      ...(shouldReduceMotion ? { duration: 0 } : menuTiming.open.panel),
      delayChildren: stagger(menuTiming.open.contentStagger, {
        startDelay: menuTiming.open.contentDelay,
      }),
    },
  }),
})

// the elements of the panel's content, they follow the panel's variant
export const contentVariants: Variants = {
  collapsed: { opacity: 0, x: -12, transition: menuTiming.close.content },
  expanded: { opacity: 1, x: 0, transition: menuTiming.open.content },
}
